<template>
    <v-autocomplete-custom
        popup-used-in-modal: popupUsedInModal
        :class="{'some-selections': anySelections}"
        :clearable="anySelections"
        :items="itemsWithDisabledItems"
        :multiple="multiple"
        :search-input.sync="searchInput"
        :value="innerValue"
        deletable-chips
        :item-text="itemText"
        item-value="value"
        v-bind="$attrs"
        @input="onAutocompleteInput"
        v-on="$listeners"
        @keydown.enter="addUserEnteredItemToSelection">
        <template #selection="data">
            <v-chip :disabled="data.item.disabled"
                    :input-value="data.selected"
                    close
                    close-icon="fas fa-circle-xmark"
                    small
                    v-bind="data.attrs"
                    @click:close="removeValueFromSelection(data.item.value)">
                <table-autocomplete-tag v-if="superTags"
                                        :value="data.item.value" />
                <template v-else>{{ data.item[itemText] }}</template>
            </v-chip>
        </template>
        <template #item="data">
            <v-list-item-content>
                <table-autocomplete-tag v-if="superTags"
                                        :value="data.item[itemText]" />
                <template v-else>{{ data.item[itemText] }}</template>
            </v-list-item-content>
            <v-list-item-action v-if="data.item.userEntered">
                <v-btn icon
                       @click.stop="removeUserEnteredItem(data.item.value)">
                    <v-icon v-html="'far fa-xmark'" />
                </v-btn>
            </v-list-item-action>
        </template>

        <template #no-data>
            <filter-empty-message />
        </template>

    </v-autocomplete-custom>
</template>

<script>
import { defineComponent } from 'vue'
import TableAutocompleteTag from './table-autocomplete-tag.vue'
import { delay } from '../../../../utility.js'

import Template from '../../elements/template.vue'
import FilterEmptyMessage from './filter-empty-message.vue';
import { arraysEqual } from '../../../../../utils/array';

export default defineComponent({
    inheritAttrs: false,
    components: {
        FilterEmptyMessage,
        Template,
        TableAutocompleteTag,
    },
    props: {
        items: Array,
        itemText: {
            type: String,
            default: 'value'
        },
        value: Array,
        superTags: {
            type: Boolean,
            default: false
        },
        multiple: {
            type: Boolean,
            default: true
        },
        userEnteredItemsEnabled: {
            type: Boolean,
            default: true
        },
        portalAnchorClass: {
            type: String,
            default: 'popper-menu-anchor popper-menu-hide-under-navbar'
        },
        popupUsedInModal: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            searchInput: '',
            selectedItems: this.value.slice(), // shallow copy
            userEnteredItemsSet: new Set(),
            userEnteredItems: [],
            itemAddedCooldown: false,
            innerValue: this.value,
            preserveValue: ''

        }
    },
    methods: {
        addUserEnteredItemToSelection() {

            if (!this.userEnteredItemsEnabled || !this.searchInput || this.itemAddedCooldown) return

            const searchInputLowercase = String(this.searchInput).toLowerCase()

            let selectedItemValue


            if (this.itemsLowercaseSet.has(searchInputLowercase) && this.selectedItemsLowercaseSet.has(searchInputLowercase)) {
                selectedItemValue = this.searchInput
            } else if (Array.from(this.itemsLowercaseSet).find(value => value.startsWith(`§${searchInputLowercase}`))) {
                selectedItemValue = `§${searchInputLowercase}` // ToDo - get name of supertag from somewhere that has original case preserved
            } else {
                let inputAlreadyInUserEnteredItems = false

                for (const userEnteredItem of this.userEnteredItemsSet.values()) {
                    if (userEnteredItem.toLowerCase() == searchInputLowercase) {
                        inputAlreadyInUserEnteredItems = true
                        break
                    }
                }

                if (!inputAlreadyInUserEnteredItems) {
                    this.userEnteredItemsSet.add(this.searchInput)
                    this.userEnteredItems = Array.from(this.userEnteredItemsSet)
                }

                selectedItemValue = this.searchInput
            }

            this.searchInput = null

            if (!selectedItemValue) return

            this.selectedItemsLowercaseSet.add(selectedItemValue.toLowerCase())

            if (this.selectedItemsLowercaseSet.size > this.selectedItems.length) {
                this.selectedItems.push(selectedItemValue)
            }

            this.startItemAddedCooldown()

        },
        removeValueFromSelection(value) {
            const index = this.selectedItems.findIndex(selectedItem => selectedItem === value)
            if (index >= 0) this.selectedItems.splice(index, 1)
            return index
        },
        removeUserEnteredItem(value) {
            this.removeValueFromSelection(value)
            this.userEnteredItemsSet.delete(value)
            this.userEnteredItems = Array.from(this.userEnteredItemsSet)
            this.searchInput = null
        },
        async onAutocompleteInput(newSelection) {
            // Hack required to not add two items to selection in the following case:
            // If text input is entered partially (ex. 'uni'), and a menu item is selected
            // with up/down arrows (ex. 'University Course 1'); when the enter button is pressed,
            // the keyboard.enter event is triggered first and the partially entered text gets added
            // to custom search tags, but also this onInput function gets triggered with both the
            // partially entered text and the selected menu item (ex. ['uni', 'University Course 1']).
            // This is Vuetify's fault, they automatically add the selected menu item to selection when
            // enter is pressed, no way to disable this behaviour. Even though the second item is not
            // emitted (so this.value is ['uni']), both inputs are still shown in the input selection
            // area as chips. The Vuetify Autocomplete component has an internal state and adds extra
            // items to their value, even though we are passing the value prop.
            // This hack is required to reset the passed value of v-autocomplete and display only the
            // actual value that should be displayed if they didn't have internal state.
            if (this.itemAddedCooldown && (newSelection.length > this.value.length)) {
                Vue.delete(newSelection, newSelection.length - 2);
                return
            }

            // Allow to clear the complete input when the X is clicked in the component
            const clearAll = newSelection.length === 0;

            if (!clearAll && newSelection.length < this.value.length) {
                this.preserveValue = this.selectedItems;
                await this.$nextTick();
                this.innerValue = this.preserveValue;
                return;
            }

            this.startItemAddedCooldown();

            this.selectedItems = newSelection;
        },
        async startItemAddedCooldown() {
            this.itemAddedCooldown = true
            await delay(2)
            this.itemAddedCooldown = false
        }

    },
    computed: {
        itemsLowercaseSet() {
            return new Set(this.items.filter(({ value }) => value).map(({ value }) => String(value).toLowerCase()))
        },
        selectedItemsLowercaseSet() {
            return new Set(this.selectedItems.map(item => String(item).toLowerCase()))
        },
        anySelections() {
            return this.selectedItems.length > 0
        },
        disabledItems() {
            const filteredItems = this.items ? this.items.filter(item => !item.header && !item.divider) : []

            // If there are more entries in `value` than there are in `items`, we have to add
            // disabled entries to `items` to show these extra entries that are present in `value`.
            // The missing entries were filtered out by `tableMixin.js -> filteredItemsWithUserEntered`
            // when other filters were applied.

            const returnValue = []

            for (const entry of this.innerValue) {

                const entryInFilteredItems = filteredItems.findIndex(filteredItem => filteredItem.value === entry) >= 0

                if (entryInFilteredItems) {
                    continue
                }

                returnValue.push({ value: entry, disabled: true })
            }

            return returnValue
        },
        itemsWithDisabledItems() {
            return Array.isArray(this.items) ? this.items.concat(this.disabledItems) : this.disabledItems
        }
    },
    watch: {
        async selectedItems(val) {
            this.$emit('input', val)
            // Update the position of the popup menu, since items were added to selection and
            // content probably reflowed.
            if (this.popperInstance) this.popperInstance.update()
        },
        userEnteredItems(val) {
            this.$emit('update:userEnteredItems', val)
        },
        value(val) {
            this.innerValue = val
            if (!arraysEqual(this.selectedItems, val)) {
                this.selectedItems = val.slice();
            }
        }
    }
})
</script>
